import {
  ajax
} from 'application/src/js/functions.js';
import {
  Mediator,
  Pagination,
  SpinnerOverlay
} from 'application/src/js/util.js';
import {
  MultiCheckboxDropdown
} from 'application/src/js/ui-collection/multi-checkbox-dropdown.js';

import * as reduxStore from './store.js';

/**
 * @class
 * @description
 * Main Search Controller
 * inits all search result tab and their respective filters
 * adds the current search params to the URL
 * controlls the position of the 'apply filter' buttons
 * @param {ResultTabs} tabs ResultTabs instance
 * @param {Notifications} notify Notifications instance
 * @param {object} store redux store functions
 * @param {Mediator} mediator Mediator instance for event communication
 * @param {object} translations object of translated texts
 */
export default class SearchController extends Mediator {
  constructor(args) {
    super();

    this.tabs = args.tabs;
    this.notify = args.notify;
    this.store = args.store;
    this.searchService = args.searchService;
    this.debugMode = args.debugMode;

    this.mediator = args.mediator;
    this.translations = args.translations;
    this.params = args.params;
    this.currentTabName = this.params['t'] || 'news';
    this.resultTypes = params['show'] ? params['show'].split(',') : [];
    this.hideNumResults = this.resultTypes.length == 1;
    this.hideClass = 'hide';

    if (this.resultTypes.length && this.resultTypes.indexOf(this.currentTabName) == -1) {
      this.currentTabName = this.resultTypes[0];
    }

    this.toggleFilterButton = document.querySelector('.toggle-filter');

    if (this.params['v'] && this.params['v'].length < 3) {
      this.notify.send(translations.generalError, 'alert');
      return;
    }

    reduxStore.injectAsyncReducer(this.store, 'general', reduxStore.generalReducer);

    this.store.dispatch({
      type: 'GENERAL.RESET',
      payload: {
        tab: this.currentTabName,
        isLoggedIn: true,
        basicIds: [] // basic ids related to the search results
      }
    });

    this.addEvents();
    this.initResultsController();
    this.controlButtonPos();
  }

  initResultsController() {
    if (!this.resultTypes.length || this.resultTypes.indexOf('news') != -1) {
      import( /* webpackChunkName: "search-news-results-controller" */ 'mundus-agri/src/js/search/news/results.js')
        .then(({
          default: NewsResultsController
        }) => {
          this.newsResultsController = new NewsResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            productFilter: new MultiCheckboxDropdown(document.querySelector('.filters.news .product-filter'), {
              parseNumeric: true
            }),
            companyFilter: new MultiCheckboxDropdown(document.querySelector('.filters.news .company-filter'), {
              parseNumeric: true
            }),
            categoryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.news .category-filter'), {
              parseNumeric: true
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.news .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.news .no-results-error'),
            }),
            translations: this.translations.news,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.news .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.news .overlay')
            }),
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('offers') != -1) {
      import( /* webpackChunkName: "search-offers-results-controller" */ 'mundus-agri/src/js/search/offers/results.js')
        .then(({
          default: OfferResultsController
        }) => {
          this.offerResultsController = new OfferResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            certificateFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .certificate-filter'), {
              parseNumeric: true
            }),
            // certifierFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .certifier-filter'), { parseNumeric: true }),
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .country-filter'), {
              parseNumeric: true
            }),
            companyFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .company-filter'), {
              parseNumeric: true
            }),
            currencyFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .currency-filter'), {
              parseNumeric: true
            }),
            processingFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .processing-filter'), {
              parseNumeric: true
            }),
            incotypeFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .incotype-filter'), {
              parseNumeric: false
            }),
            packagingFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .packaging-filter'), {
              parseNumeric: true
            }),
            categoryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.offers .category-filter'), {
              parseNumeric: false
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.offers .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.offers .no-results-error'),
            }),
            translations: this.translations.offers,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.offers .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.offers .overlay')
            }),
            notify: this.notify,
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('enquiries') != -1) {
      import( /* webpackChunkName: "search-enquiries-results-controller" */ 'mundus-agri/src/js/search/enquiries/results.js')
        .then(({
          default: EnquiryResultsController
        }) => {
          this.enquiryResultsController = new EnquiryResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            certificateFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .certificate-filter'), {
              parseNumeric: true
            }),
            // certifierFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .certifier-filter'), { parseNumeric: true }),
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .country-filter'), {
              parseNumeric: true
            }),
            companyFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .company-filter'), {
              parseNumeric: true
            }),
            processingFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .processing-filter'), {
              parseNumeric: true
            }),
            incotypeFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .incotype-filter'), {
              parseNumeric: false
            }),
            packagingFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .packaging-filter'), {
              parseNumeric: true
            }),
            categoryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.enquiries .category-filter'), {
              parseNumeric: true
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.enquiries .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.enquiries .no-results-error'),
            }),
            translations: this.translations.enquiries,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.enquiries .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.enquiries .overlay')
            }),
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('companies') != -1) {
      Promise.all([
          import( /* webpackChunkName: "search-companies-results-controller" */ 'mundus-agri/src/js/search/companies/results.js'),
          import( /* webpackChunkName: "search-map-controller" */ 'mundus-agri/src/js/search/maps/map-controller.js')
        ])
        .then(([{
            default: CompaniesResultsController
          },
          {
            default: MapController
          }
        ]) => {
          this.companiesResultsController = new CompaniesResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.companies .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.companies .no-results-error')
            }),
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.companies .country-filter'), {
              parseNumeric: true
            }),
            valueAddedStepsFilter: new MultiCheckboxDropdown(document.querySelector('.filters.companies .vas-filter'), {
              parseNumeric: true
            }),
            notify: this.notify,
            translations: translations.companies,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.companies .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.companies .overlay')
            }),
            debugMode: this.debugMode
          });

          this.mapController = new MapController({
            mediator: this.mediator,
            store: this.store,
            params: params,
            translations: translations.companies,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.maps .overlay')
            }),
            debugMode: this.debugMode
          });

        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('cropdates') != -1) {
      import( /* webpackChunkName: "search-cropdates-results-controller" */ 'mundus-agri/src/js/search/cropdates/results.js')
        .then(({
          default: CropdatesResultsController
        }) => {
          this.cropdatesResultsController = new CropdatesResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            rawMaterialFilter: new MultiCheckboxDropdown(document.querySelector('.filters.cropdates .raw-material-filter'), {
              parseNumeric: true
            }),
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.cropdates .country-filter'), {
              parseNumeric: true
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.cropdates .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.cropdates .no-results-error'),
            }),
            translations: this.translations.cropdates,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.cropdates .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.cropdates .overlay')
            }),
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('charts') != -1) {
      import( /* webpackChunkName: "search-charts-results-controller" */ 'mundus-agri/src/js/search/charts/results.js')
        .then(({
          default: ChartResultsController
        }) => {
          this.chartResultsController = new ChartResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            productFilter: new MultiCheckboxDropdown(document.querySelector('.filters.charts .product-filter'), {
              parseNumeric: true
            }),
            incotypeFilter: new MultiCheckboxDropdown(document.querySelector('.filters.charts .incotype-filter'), {
              parseNumeric: false
            }),
            exchangeMarketFilter: new MultiCheckboxDropdown(document.querySelector('.filters.charts .exchange-market-filter'), {
              parseNumeric: false
            }),
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.charts .country-filter'), {
              parseNumeric: true
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.charts .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.charts .no-results-error'),
            }),
            translations: this.translations.charts,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.charts .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.charts .overlay')
            }),
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }

    if (!this.resultTypes.length || this.resultTypes.indexOf('contacts') != -1) {
      import( /* webpackChunkName: "search-contacts-results-controller" */ 'mundus-agri/src/js/search/contacts/results.js')
        .then(({
          default: ContactResultsController
        }) => {
          this.contactResultsController = new ContactResultsController({
            mediator: this.mediator,
            multiCheckboxDropdownOptionTemplate: multiCheckboxDropdownOptionTemplate,
            countryFilter: new MultiCheckboxDropdown(document.querySelector('.filters.contacts .country-filter'), {
              parseNumeric: true
            }),
            valueAddedStepsFilter: new MultiCheckboxDropdown(document.querySelector('.filters.contacts .vas-filter'), {
              parseNumeric: true
            }),
            productFilter: new MultiCheckboxDropdown(document.querySelector('.filters.contacts .product-filter'), {
              parseNumeric: true
            }),
            params: params,
            pagination: new Pagination({
              container: document.querySelector('.search-results.contacts .search-results-pagination'),
              noResultsMessage: document.querySelector('.search-results.contacts .no-results-error'),
            }),
            translations: this.translations.contacts,
            store: this.store,
            loader: new SpinnerOverlay({
              element: document.querySelector('.search-results.contacts .overlay')
            }),
            filterLoader: new SpinnerOverlay({
              element: document.querySelector('.filters.contacts .overlay')
            }),
            debugMode: this.debugMode
          });
        })
        .catch(e => {
          if (this.debugMode && e) console.log(e);
        });
    }
  }

  addEvents() {
    /**
     * @description adjust the search button's position in order to keep them in the viewport
     */
    ['scroll', 'resize'].map(eventType => {
      window.addEventListener(eventType, e => {
        new Promise((resolve, reject) => {
          if (this.repositionTimeout) clearTimeout(this.repositionTimeout);
          this.repositionTimeout = setTimeout(_ => resolve(), 25);
        }).then(_ => this.controlButtonPos());
      });
    });

    /**
     * @description published from each search result controller
     * @param {string} tabName
     */
    this.mediator.subscribe('onBeforeSearch', tabName => {
      if (this.store.getState().general.isLoggedIn === true) {
        let tabNames = [tabName];

        if (tabNames.indexOf(this.currentTabName) == -1) tabNames.push(this.currentTabName);

        [...this.tabs.labels, this.tabs.toggleLabel]
        .filter(label => tabNames.indexOf(label.getAttribute('data-result-type')) >= 0)
          .map(label => {
            label.classList.remove('has-results');
            label.classList.add('loading');
            const resultsOut = label.querySelector('.num-results')
            if (resultsOut) resultsOut.innerHTML = 0;
          });
      }

      this.updateRoute();
    });

    /**
     * @description published from each search result controller
     * @param {string} tabName
     * @param {number} numResults
     * @param {boolean} isLoggedIn
     */
    this.mediator.subscribe('onResultsLoad', (tabName, numResults = 0, isLoggedIn) => {
      this.store.dispatch({
        type: 'GENERAL.IS_LOGGED_IN',
        payload: isLoggedIn
      });

      let tabNames = [tabName];
      if (tabName == 'maps') tabNames.push('companies');
      if (tabName == 'companies') tabNames.push('maps');

      [...this.tabs.labels, this.tabs.toggleLabel]
        .filter(label => tabNames.indexOf(label.getAttribute('data-result-type')) >= 0)
        .map(label => {
          const resultsOut = label.querySelector('.num-results')
          if (this.hideNumResults) resultsOut.classList.add(this.hideClass);
          if (resultsOut) resultsOut.innerHTML = numResults;
          label.classList.remove('loading');
          if (numResults > 0) label.classList.add('has-results');
        });

      if (this.currentTabName === tabName) this.saveSearch();

      this.publish('searchFinished', this.store.getState(), tabName);
    });

    this.toggleFilterButton.addEventListener('click', e => {
      e.currentTarget.classList.toggle('expanded');
      const isExpanded = e.currentTarget.classList.contains('expanded');
      document.querySelector('.filter-column .content-box').style.display = isExpanded ? 'block' : 'none';
      this.controlButtonPos();
    });

    this.tabs.subscribe('change', o => {
      const resultType = o.activeLabel.getAttribute('data-result-type');
      this.currentTabName = resultType;
      const currentActiveFilterBox = document.querySelector('.filters.active');
      const currentActiveResultsBox = document.querySelector('.search-results.active');
      const nextActiveFilterBox = document.querySelector(`.filters.${resultType}`);
      const nextActiveResultsBox = document.querySelector(`.search-results.${resultType}`);

      if (currentActiveFilterBox) currentActiveFilterBox.classList.remove('active');
      if (currentActiveResultsBox) currentActiveResultsBox.classList.remove('active');
      if (nextActiveFilterBox) nextActiveFilterBox.classList.add('active');
      if (nextActiveResultsBox) nextActiveResultsBox.classList.add('active');

      if (resultType === 'maps') {
        document.querySelector(`.filters.companies`).classList.add('active');
      }

      this.controlButtonPos();
      this.updateRoute();
      this.saveSearch();

      this.store.dispatch({
        type: 'GENERAL.TAB',
        payload: this.currentTabName
      });
    });

    this.mediator.subscribe('onFullscreenOpen', _ => this.controlButtonPos())
  }

  /**
   * @description controls the position of the search submit buttons in order to keep them always in the viewport
   */
  controlButtonPos() {
    const outerFilterContainer = document.querySelector('.filter-column .content-box');
    outerFilterContainer.style.maxHeight = window.innerHeight - outerFilterContainer.getBoundingClientRect().top + 'px';

    const submitSearchOverlayButton = document.querySelector(`.search-results.${this.currentTabName} .button-overlay .button`);

    if (submitSearchOverlayButton) {
      const outerResultsContainer = document.querySelector(`#main-content-container`);
      const containerHeight = outerResultsContainer.getBoundingClientRect().height;
      const buttonHeight = submitSearchOverlayButton.getBoundingClientRect().height;
      const topMostPos = 10;
      const bottomMostPos = containerHeight - buttonHeight - 10;

      let buttonPos = window.innerHeight / 2 - outerResultsContainer.getBoundingClientRect().top;

      if (buttonPos < topMostPos) buttonPos = topMostPos;
      if (buttonPos > bottomMostPos) buttonPos = bottomMostPos;

      submitSearchOverlayButton.style.top = buttonPos + 'px';
    }
  }

  /**
   * @description saves the current search parameters in db
   */
  saveSearch() {
    const state = this.store.getState();

    let tabState = state[this.currentTabName];
    if (this.currentTabName === 'maps') tabState = state['companies'];

    if (!tabState) return;
    tabState['t'] = this.currentTabName;
    tabState['v'] = decodeURI(params.v);

    ajax('/search/ajaxLogSearch', tabState)
      .then(_ => {})
      .catch(e => {
        if (this.debugMode) console.log(e);
      });
  }

  /**
   * @description replace the url (history) in order to update the GET params
   */
  updateRoute() {
    const state = this.store.getState();
    let tabState = state[this.currentTabName];
    if (this.currentTabName === 'maps') tabState = state['companies'];
    if (!tabState) return;
    let routeParams = this.getRouteParamsFromState(tabState);
    if (this.resultTypes.length) routeParams += `&show=${this.params.show}`;
    history.replaceState({}, document.title, `/search?${routeParams}`);
  }

  /**
   * @description transforms the passed Redux state into a GET param string
   * @returns {string} GET param string
   * @param {Object} state redux state
   */
  getRouteParamsFromState(state) {
    if (!state) return '';

    // general parameters
    const paramStrings = [];
    paramStrings.push(`t=${encodeURIComponent(this.currentTabName)}`);
    paramStrings.push(`v=${encodeURIComponent(params.v)}`);

    // tab related filter parameters
    // "date_range", "amount" and all filters without selected values are skipped
    const filterParamStrings = Object.keys(state).reduce((collectedFilterParams, filterName) => {
      if (state[filterName] && filterName.match(/^(amount|date_range|t|v)$/) === null) {

        // prevent adding empty params to the route
        const hasValue = (state[filterName] instanceof Array && state[filterName].length) ||
          (typeof state[filterName] === 'number' && state[filterName] > 0) ||
          (typeof state[filterName] === 'string' && state[filterName].length);

        if (hasValue) {
          const paramString = state[filterName] instanceof Array ? state[filterName].join(',') : encodeURIComponent(state[filterName].toString());
          collectedFilterParams.push(`${filterName}=${paramString}`);
        }
      }
      return collectedFilterParams;
    }, []);

    return [...paramStrings, ...filterParamStrings].join('&');
  }
}

/**
 * @description returns the template for a MultiCheckboxDropdown option
 * @param {Object} d JSON data for the option
 * @param {Object} keyMap data mapper object
 * @param {string} checked checked status of the option's checkbox
 * @param {string} disabled disable status of the option's checkbox
 * @param {string} idPrefix prefix string for all ID attributes being used in the dropdown
 */
const multiCheckboxDropdownOptionTemplate = (d, keyMap, checked = false, disabled = false, idPrefix = '') => {
  return /*html*/`
    <label for="${idPrefix}-${d[keyMap.id]}" class="uic-option-row ${disabled ? `not-available` : ``}">
      <div>
        <div class="uic-pretty-checkbox">
          <input
            type="checkbox"
            id="${idPrefix}-${d[keyMap.id]}"
            value="${d[keyMap.value]}"
            ${checked ? `checked` : ``}
          >
          <label for="${idPrefix}-${d[keyMap.id]}"></label>
        </div>
      </div>
      <div class="option-text">
        <label for="${idPrefix}-${d[keyMap.id]}">
          ${d[keyMap.label]}
        </label>
      </div>
    </label>
  `;
};